import { Button, Chip, FormValues, CollapsibleSectionContainerV2 } from '@stern-ui/ui';
import { formatDate } from '@stern-ui/utilities';
import clsx from 'clsx';
import { useSelector, useDispatch } from 'react-redux';

import filterLockIcon from '@/assets/filter-lock.svg';
import {
	FilterValuesObj,
	PatientListFilterValues,
	filterValue,
	isFiltersApplied,
	resetDataTableStateValues,
	saveFilterValues,
	selectFilterValues,
	valueHasFilterApplied,
} from '@/components/PatientDataTable';
import { studyFiltersObject } from '@/features/patientAssignment';
import {
	hasCompletedSurvey,
	ContactFiltersType,
	PatientListFilterKey,
	SurveyFiltersType,
	PatientListFilterRequest,
} from '@/features/patientLists';
import { trackEvent } from '@/lib/analytics';
import { statusOptions } from '@/utils/filterOptions';

import { KEY_MAP, VALUE_MAP } from '../PatientDataTable/consts/';

type SummaryFilterProps = {
	searchValue?: string;
	saveSearch?: (newSearch: string) => void;
	isCollapsible?: boolean;
	isUpdatable?: boolean;
	readOnly?: boolean;
	filters?: FormValues | undefined;
	showSubtitle?: boolean;
	excludeFromSummary?: string[];
};

export const SummaryFilter = (props: SummaryFilterProps) => {
	const {
		searchValue,
		saveSearch,
		isCollapsible = false,
		isUpdatable = true,
		readOnly = false,
		filters,
		showSubtitle = true,
		excludeFromSummary,
	} = props;
	const dispatch = useDispatch();
	const reduxFilters = useSelector(selectFilterValues);

	const filtersToUse = readOnly ? filters : reduxFilters;
	// exclude any filters included in the excludeFromSummary key array
	const filterValues =
		excludeFromSummary && filtersToUse
			? Object.fromEntries(
					Object.entries(filtersToUse).filter(
						([key]) => !excludeFromSummary.includes(key)
					)
				)
			: filtersToUse;

	const remove = (key: PatientListFilterKey, value?: string | number) => {
		if (key === 'search') {
			// reset the search value
			saveSearch && saveSearch('');
		} else {
			// create a copy of the filterValues object and store it in a new variable newFilterValues.
			const newFilterValues = { ...filterValues } as PatientListFilterRequest;
			const values = newFilterValues[key] as PatientListFilterValues;
			const removeValue = typeof value === 'string' ? value.toLocaleLowerCase() : value;
			const hasValue = typeof value === 'string' ? !!value : Number.isInteger(value);
			//  check if the value associated with the given key is an array and whether a value has been provided.
			if (Array.isArray(values) && hasValue) {
				// filter out the values that match the provided value (case-insensitive) and updates the newFilterValues object accordingly. If the value is an object, it filters based on the name property.
				(newFilterValues[key] as PatientListFilterValues) = values.filter(
					(v: PatientListFilterValues, index) => {
						if (Number.isInteger(value)) {
							// remove from array using index
							return index !== value;
						}
						if (typeof v === 'string') {
							if (key === 'status') {
								return VALUE_MAP[v].toLocaleLowerCase() !== removeValue;
							}
							return v.replaceAll('_', ' ').toLocaleLowerCase() !== removeValue;
						}
						return (v as filterValue).name !== value;
					}
				);
			} else {
				// If the value associated with the given key is not an array or if no value has been provided, it deletes the key from the newFilterValues object.
				delete newFilterValues[key];
			}
			if (key === 'study_id') {
				// if study is removed, remove study status as well
				delete newFilterValues['status'];
			} else if (key === 'survey_filters') {
				// if survey filter is removed, check if we no longer have any completed surveys
				const surveyFilters =
					newFilterValues.survey_filters && newFilterValues.survey_filters.length > 0
						? {
								survey_filters: newFilterValues.survey_filters.map(
									(surveyFilter) => {
										return JSON.parse(surveyFilter as string);
									}
								),
							}
						: {};
				if (!hasCompletedSurvey(surveyFilters as FormValues)) {
					// no completed survey filters so remove needs assistance filter
					delete newFilterValues['survey_needs_assistance'];
				}
			}
			dispatch(saveFilterValues({ filterValues: newFilterValues }));
		}
		trackEvent('Data table filter removed');
	};

	const resetAll = () => {
		// reset everything in state except for filters in the excludeFromSummary array
		const filtersToPreserve = excludeFromSummary
			? Object.fromEntries(
					Object.entries(filtersToUse as FilterValuesObj).filter(([key]) =>
						excludeFromSummary.includes(key)
					)
				)
			: {};
		dispatch(resetDataTableStateValues({ filterValues: filtersToPreserve }));
		saveSearch && saveSearch('');
	};

	const hasFiltersApplied = isFiltersApplied(filterValues as FilterValuesObj);
	const hasSearchApplied = searchValue && searchValue.length > 0;

	if (!hasFiltersApplied && !hasSearchApplied) {
		return null;
	}

	const renderFilterChips = () => {
		return Object.entries(filterValues as PatientListFilterValues)
			.filter(([, value]) => valueHasFilterApplied(value))
			.map(([key, value]) => {
				let val = value;
				if (Array.isArray(val)) {
					// If the first element in the array is not a string, map each element in the array to its "name" property.
					if (typeof value[0] !== 'string') {
						val = value.map((v: { name: string }) => {
							if (key === 'contact_filters') {
								// 'name' in contact filters has a different meaning (it's the contact type name, not the filter name)
								return v;
							}
							return v.name || v;
						});
					} else {
						// Otherwise, map each string in the array to its corresponding value in the VALUE_MAP object.
						val = value.map((v: string) => VALUE_MAP[v] || v);
					}
					if (key === 'age' || key === 'ageMonth' || key === 'ageYear') {
						// display as range
						return (
							<Chip
								key={`${key}${val}`}
								onClose={
									isUpdatable
										? () => remove(key as PatientListFilterKey)
										: undefined
								}
								name={KEY_MAP[key]}
								value={`${value[0]} - ${value[1]}`}
							/>
						);
					}
					return val.map(
						(
							v: string | SurveyFiltersType | ContactFiltersType | studyFiltersObject,
							index: number
						) => {
							let thisKey = `${key}${v}`;
							let thisRemoveId = v as string | number;
							let thisValue = v as string;
							if (
								key === 'survey_filters' ||
								key === 'contact_filters' ||
								key === 'study_filters'
							) {
								// if using Redux state then we need to parse value as it has been saved as a string
								// if readOnly (using filters prop) then no need to parse
								const thisFilter = readOnly
									? v
									: (JSON.parse(v as string) as
											| SurveyFiltersType
											| ContactFiltersType);
								thisKey = `${key}-${index}`;
								thisRemoveId = index;
								if (key === 'survey_filters') {
									// survey filter
									thisValue = `${
										(thisFilter as SurveyFiltersType).filter_type === 'include'
											? 'Include'
											: 'Exclude'
									} '${(thisFilter as SurveyFiltersType).survey_id?.name}' was ${
										(thisFilter as SurveyFiltersType).survey_status
									}`;
								} else if (key === 'contact_filters') {
									// contact filter
									thisValue = `Patient ${
										(thisFilter as ContactFiltersType).is_null === 'true'
											? 'does not have'
											: 'has'
									} ${
										VALUE_MAP[(thisFilter as ContactFiltersType).name] ||
										(thisFilter as ContactFiltersType).name
									}`;
								} else {
									// study filter
									const selectedStatusName = (
										thisFilter as studyFiltersObject
									).status?.map((s) => {
										const statusOption = statusOptions.find(
											(option) => option.value === s
										);
										return statusOption ? statusOption.label : s;
									});
									thisValue = `${
										(thisFilter as studyFiltersObject).filter_type === 'include'
											? 'Include'
											: 'Exclude'
									} '${(thisFilter as studyFiltersObject).study?.name}' with status ${selectedStatusName?.join(
										', '
									)}`;
								}
							}
							return (
								<Chip
									key={thisKey}
									onClose={
										isUpdatable
											? () =>
													remove(
														key as PatientListFilterKey,
														thisRemoveId
													)
											: undefined
									}
									name={KEY_MAP[key]}
									value={thisValue}
								/>
							);
						}
					);
				}
				if (key === 'ageDobFrom') {
					// display date of birth as a range
					const toDate = (filterValues as FilterValuesObj)?.ageDobTo as string;
					return (
						<Chip
							key={`${key}${val}`}
							onClose={
								isUpdatable ? () => remove(key as PatientListFilterKey) : undefined
							}
							name={KEY_MAP[key]}
							value={`${formatDate(value)} - ${formatDate(toDate)}`}
						/>
					);
				}
				if (key === 'ageDobTo') {
					// this chip will be rendered as part of the ageDobFrom chip, so we can skip rendering it here
					return null;
				}
				// Any fields that need to show explicit values rather than mapping to VALUE_MAP can be added to showExplicitValuesKeyArr
				const showExplicitValuesKeyArr = ['max_results'];
				const doShowExplicitValue = showExplicitValuesKeyArr.includes(key);
				return (
					<Chip
						key={`${key}${val}`}
						onClose={
							isUpdatable ? () => remove(key as PatientListFilterKey) : undefined
						}
						name={KEY_MAP[key]}
						value={doShowExplicitValue ? value : VALUE_MAP[value] || value}
						variant={typeof value === 'boolean' ? 'noIntersection' : 'withIntersection'}
					/>
				);
			});
	};

	const renderDefaultSummaryFilter = (searchOnly = false) => {
		// searchOnly can be passed in to only show the search chip and not the filter chips.
		// This is used when the summary filter is readOnly and we want to show the search chip below the filter chips.
		const thisIsUpdatable = searchOnly || isUpdatable;
		return (
			<>
				<div className='flex flex-wrap gap-4 mb-4'>
					{!searchOnly && renderFilterChips()}
					{hasSearchApplied && (
						<Chip
							key='search-chip'
							onClose={thisIsUpdatable ? () => remove('search') : undefined}
							name='Search'
							value={searchValue}
						/>
					)}
				</div>
				{thisIsUpdatable && (
					<div className='mb-4'>
						<Button type='reset' variant='secondary' onClick={resetAll}>
							Clear {!hasFiltersApplied || searchOnly ? 'search' : 'all filters'}
						</Button>
					</div>
				)}
			</>
		);
	};

	const renderReadonlySummaryFilter = () => {
		return (
			<>
				{renderFilterChips()}
				{isUpdatable && (
					<div className='mb-4'>
						<Button type='reset' variant='secondary' onClick={resetAll}>
							Clear all filters
						</Button>
					</div>
				)}
			</>
		);
	};

	const renderCollapsibleSummaryContainer = () => {
		return (
			<>
				<CollapsibleSectionContainerV2
					title='Filter criteria'
					classNames='mb-4 bg-white px-0'
					dataTestId='summary-filter'
					titleIcon={
						readOnly ? <img src={filterLockIcon} alt='Filter lock icon' /> : undefined
					}
					titleIconAlign='right'
				>
					<div
						className={clsx(
							'flex flex-wrap w-full',
							isUpdatable ? 'justify-between' : 'justify-start',
							'border-2 border-secondaryBorder rounded-md gap-4 p-4'
						)}
						data-test-id='filter-summary'
					>
						{readOnly && showSubtitle
							? renderReadonlySummaryFilter()
							: renderDefaultSummaryFilter()}
					</div>
				</CollapsibleSectionContainerV2>
				{readOnly && hasSearchApplied && (
					<div
						className='flex flex-wrap justify-between w-full'
						data-test-id='filter-summary-search-only'
					>
						{renderDefaultSummaryFilter(true)}
					</div>
				)}
			</>
		);
	};

	const renderDefaultSummaryContainer = () => {
		return (
			<div className='flex flex-wrap justify-between w-full' data-test-id='filter-summary'>
				{readOnly && showSubtitle
					? renderReadonlySummaryFilter()
					: renderDefaultSummaryFilter()}
			</div>
		);
	};

	return (
		<>{isCollapsible ? renderCollapsibleSummaryContainer() : renderDefaultSummaryContainer()}</>
	);
};
